import { Badge, Button, Checkbox, Field, Input } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { PasswordField } from './ui/PasswordField';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface VoiceConfig { hasKey: boolean; hint: string | null; enabled: boolean; model: string }

/** Plataforma › Integrações: chave da ElevenLabs (botão Ouvir dos agentes). Vale para as organizações sem chave própria. */
export function VoiceProviderCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [cfg, setCfg] = useState<VoiceConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ apiKey: '', model: '', enabled: true });
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/voice-config');
    if (res.ok) setCfg(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const call = async (kind: 'save' | 'test') => {
    setBusy(kind);
    try {
      const res = await fetchApi(kind === 'save' ? '/api/tools/voice-config' : '/api/tools/voice-config/test', {
        method: kind === 'save' ? 'PUT' : 'POST',
        body: JSON.stringify(kind === 'save' ? form : { apiKey: form.apiKey || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir');
      if (kind === 'save') { setCfg(body); setForm((f) => ({ ...f, apiKey: '' })); toast.success('Voz da plataforma salva'); }
      else toast.success(body.message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
    } finally {
      setBusy(null);
    }
  };

  const active = !!cfg?.enabled;
  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center' }}><ICONS.Key size={24} /></span>
            {active ? <Badge variant="green">Ativo</Badge> : cfg?.hasKey ? <Badge variant="neutral">Desligado</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>Voz (ElevenLabs)</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Botão Ouvir dos agentes, para quem não tem chave própria</div>
          <div className="integration-host">
            <ICONS.Key size={16} />
            <span>{cfg?.hasKey ? `Chave ${cfg.hint} · ${cfg.model}` : 'sem chave'}</span>
          </div>
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={cfg?.hasKey ? ICONS.Edit : ICONS.Plus} onClick={() => { setForm({ apiKey: '', model: cfg?.model || '', enabled: cfg?.hasKey ? cfg.enabled : true }); setOpen(true); }}>
            {cfg?.hasKey ? 'Editar' : 'Configurar'}
          </Button>
        </div>
      </div>

      <SidePanel open={open} onClose={() => setOpen(false)} title="Voz da plataforma (ElevenLabs)" description="Na ElevenLabs, em Profile › API Keys, crie uma chave e cole aqui. Ela fica cifrada e só o servidor a usa. Cada agente escolhe a voz na aba Voz e pode trazer uma chave própria." formId="voice-form" submitting={busy === 'save'}
        footerStart={<Button variant="secondary" icon={ICONS.Play} type="button" loading={busy === 'test'} disabled={!form.apiKey && !cfg?.hasKey} onClick={() => call('test')}>Testar chave</Button>}>
        <form id="voice-form" noValidate onSubmit={(e) => { e.preventDefault(); call('save'); }} className="flex flex-col gap-4">
          <PasswordField label="Chave de API" placeholder={cfg?.hasKey ? 'Deixe em branco para manter a atual' : ''} value={form.apiKey}
            onChange={(value) => setForm({ ...form, apiKey: value.trim() })} autoComplete="off" />
          <Field label="Modelo" description="Padrão: eleven_multilingual_v2 (português)">
            <Input onChange={(e) => setForm({ ...form, model: e.target.value.trim() })} value={form.model} />
          </Field>
          <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, enabled: checked })} checked={form.enabled} label={<>Ativo</>} />
        </form>
      </SidePanel>
    </>
  );
}
